import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Plus, Star, ClipboardList, UtensilsCrossed, CalendarCheck, PartyPopper } from "lucide-react";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import IngredientsShowcase from "../components/IngredientsShowcase";
import { useTray } from "../context/TrayContext";

const POPULAR = [
  { name: "Chicken Biryani", desc: "Fragrant basmati layered with tender chicken", full: 130, img: "https://images.unsplash.com/photo-1631515243349-e0cb75fb8d3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
  { name: "Goat Nihari", desc: "Slow-cooked overnight till it falls apart", full: 170, img: "https://images.unsplash.com/photo-1606843046080-45bf7a23c39f?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
  { name: "Chicken Karahi", desc: "Rich, spicy and full of tomato-ginger flavour", full: 130, img: "https://images.unsplash.com/photo-1631292784640-2b24be784d5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
  { name: "Goat Biryani", desc: "Our signature — saffron rice & fall-off-bone goat", full: 160, img: "https://images.unsplash.com/photo-1589302168068-964664d93dc0?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
];

const STEPS = [
  { icon: ClipboardList, title: "Choose your dishes", body: "Browse our desi menu and pick the ones you love." },
  { icon: UtensilsCrossed, title: "Pick your tray size", body: "Half or full trays — sized to your guest count." },
  { icon: CalendarCheck, title: "Set date & pickup/delivery", body: "Tell us when and how you'd like your feast." },
  { icon: PartyPopper, title: "We cater, you relax", body: "We cook fresh and bring the flavour to you." },
];

const REVIEWS = [
  { quote: "The food was absolutely amazing! Our guests are still talking about the biryani and the nihari.", name: "Ayesha R." },
  { quote: "Cooked for our wedding of 200 — everything fresh, hot and on time. True desi flavour.", name: "Bilal & Sana" },
  { quote: "Feels exactly like home cooking. The goat korma tasted like my nani used to make.", name: "Omar K." },
];

const reveal = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 } }),
};

const PopularCard = ({ dish, i }) => {
  const { add } = useTray();
  return (
    <motion.div variants={reveal} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} className="group flex flex-col overflow-hidden rounded-2xl border border-nosh-border bg-white transition-shadow duration-300 hover:shadow-[0_16px_34px_-22px_rgba(36,32,26,0.45)]">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={dish.img} alt={dish.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-bold text-nosh-ink">{dish.name}</h3>
        <p className="mt-1 flex-1 font-body text-[13px] leading-snug text-nosh-ink/55">{dish.desc}</p>
        <div className="mt-3 flex items-center justify-between">
          <div>
            <span className="font-poster text-[9px] font-bold uppercase tracking-wide text-nosh-ink/45">Full Tray</span>
            <div className="font-display text-lg font-bold text-nosh-green">${dish.full}</div>
          </div>
          <button
            data-testid={`popular-add-${dish.name.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => { add(dish, "Full Tray"); toast.success(`${dish.name} added to your order`); }}
            className="grid h-10 w-10 place-items-center rounded-full bg-nosh-green text-nosh-cream transition-transform duration-300 hover:scale-110"
            aria-label={`Add ${dish.name}`}
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />

      {/* Popular dishes */}
      <section data-testid="featured-section" className="bg-nosh-cream py-14 md:py-20">
        <div className="mx-auto max-w-[1300px] px-5 md:px-10">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-script text-2xl text-nosh-gold">Straight from the pot</div>
              <h2 className="font-display text-3xl font-bold leading-tight text-nosh-ink sm:text-4xl">Popular dishes</h2>
            </div>
            <Link to="/menu" data-testid="featured-view-all" className="group flex items-center gap-2 font-poster text-sm font-bold uppercase tracking-wide text-nosh-green transition-colors hover:text-nosh-gold">
              View full menu
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {POPULAR.map((d, i) => <PopularCard key={d.name} dish={d} i={i} />)}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section data-testid="how-section" className="bg-nosh-green py-14 md:py-20">
        <div className="mx-auto max-w-[1300px] px-5 md:px-10">
          <div className="mb-10 text-center">
            <div className="font-script text-2xl text-nosh-gold">Simple as can be</div>
            <h2 className="font-display text-3xl font-bold leading-tight text-nosh-cream sm:text-4xl">How it works</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.title} variants={reveal} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative rounded-2xl border border-nosh-border bg-white p-5">
                  <span className="absolute right-4 top-3 font-display text-4xl font-bold text-nosh-green/10">{i + 1}</span>
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-nosh-green/10 text-nosh-green"><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-display text-lg font-bold text-nosh-ink">{s.title}</h3>
                  <p className="mt-1.5 font-body text-[13px] leading-snug text-nosh-ink/55">{s.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <IngredientsShowcase />

      {/* Story teaser */}
      <section data-testid="story-teaser" className="bg-nosh-surface py-14 md:py-20">
        <div className="mx-auto grid max-w-[1300px] items-center gap-10 px-5 md:px-10 lg:grid-cols-2 lg:gap-16">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative order-2 lg:order-1">
            <div className="relative aspect-[4/3] overflow-hidden rounded-[1.5rem] border border-nosh-border shadow-xl">
              <img src="/harris-kanwal.png" alt="Harris and Kanwal at their Nosh Catering booth" className="h-full w-full object-cover object-[50%_20%]" />
            </div>
            <div className="absolute -right-3 -top-4 rounded-2xl bg-white px-4 py-2 font-script text-xl text-nosh-green shadow-xl md:-right-6">Harris &amp; Kanwal</div>
          </motion.div>
          <div className="order-1 lg:order-2">
            <div className="font-script text-2xl text-nosh-gold">Our little kitchen</div>
            <h2 className="font-display text-3xl font-bold leading-tight text-nosh-ink sm:text-4xl">A whole lot of love on every tray</h2>
            <p className="mt-4 font-body text-base leading-relaxed text-nosh-ink/70">
              We're Harris and Kanwal — partners in life and at the stove. The recipes we cook are
              the ones our mothers and grandmothers pressed into our hands, seasoned with stories and
              a pinch of nostalgia. Nosh is our way of sharing that table with you.
            </p>
            <Link to="/about" data-testid="story-read-more" className="group mt-6 inline-flex items-center gap-2.5 rounded-full border-2 border-nosh-ink/20 px-5 py-3 font-poster text-sm font-bold uppercase tracking-wide text-nosh-ink transition-colors duration-300 hover:border-nosh-green hover:text-nosh-green">
              Read our story
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section data-testid="reviews-section" className="bg-nosh-cream py-14 md:py-20">
        <div className="mx-auto max-w-[1300px] px-5 md:px-10">
          <div className="mb-10 text-center">
            <div className="font-script text-2xl text-nosh-gold">Kind words</div>
            <h2 className="font-display text-3xl font-bold leading-tight text-nosh-ink sm:text-4xl">Loved by our guests</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <motion.div key={r.name} variants={reveal} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-2xl border border-nosh-border bg-white p-6">
                <div className="flex gap-1 text-nosh-gold">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-nosh-gold" />)}</div>
                <p className="mt-3 font-body text-[15px] leading-relaxed text-nosh-ink/75">"{r.quote}"</p>
                <p className="mt-4 font-poster text-xs font-bold uppercase tracking-widest text-nosh-green">— {r.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-nosh-green py-14 md:py-20">
        <div className="relative mx-auto max-w-[1000px] px-5 text-center md:px-10">
          <div className="font-script text-2xl text-nosh-gold">Let's feast together</div>
          <h2 className="font-display text-3xl font-bold leading-tight text-nosh-cream sm:text-4xl lg:text-5xl">Cater your next gathering</h2>
          <p className="mx-auto mt-3 max-w-xl font-body text-base text-nosh-cream/75">
            Weddings, birthdays, corporate lunches or a big family dinner — build your order, pick a
            date, and we'll take care of the rest.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/menu#builder" className="rounded-full bg-nosh-gold px-6 py-3.5 font-poster text-sm font-bold uppercase tracking-wide text-white transition-transform duration-300 hover:-translate-y-1">Build your order</Link>
            <a href="tel:2016204376" className="rounded-full border-2 border-nosh-cream/40 px-6 py-3.5 font-poster text-sm font-bold uppercase tracking-wide text-nosh-cream transition-colors duration-300 hover:border-nosh-gold hover:text-nosh-gold">Call 201-620-4376</a>
          </div>
        </div>
      </section>
    </div>
  );
}
